import { useCallback } from 'react';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { createAiBuilderUserMessageId } from '../stores/aiBuilderMessageState';
import {
  createAiBuilderClientRequestId,
  createMessageTurnRequest,
} from '../stores/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../stores/aiBuilderUsagePolicy';
import {
  type AiBuilderAction,
  type AiBuilderChatState,
  type AiBuilderSelectedQuizContext,
  type AiBuilderSubmitPromptOptions,
  type AiBuilderUserMessageRetryRequest,
} from '../stores/types';
import { type useDispatchServerTurn } from '../stores/useAiBuilderServerTurnDispatcher';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { type AiBuilderTurnRequest } from '../utils/types';

type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;
type CreateTurnContextParams = {
  signal?: AbortSignal;
  selectedQuizContext?: AiBuilderSelectedQuizContext | null;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderSubmitAssistantActionParams = {
  appendUserMessage: (params: {
    content: string;
    requestId: number;
    retry?: AiBuilderUserMessageRetryRequest;
  }) => void;
  closePanel: () => void;
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  getChatState: () => AiBuilderChatState;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  getNextRequestId: GetNextAiBuilderRequestId;
  selectedQuizContext: AiBuilderSelectedQuizContext | null;
  submitUndoLastAiAction: (prompt: string) => void;
};

export const useAiBuilderSubmitAssistantAction = ({
  appendUserMessage,
  closePanel,
  copy,
  createTurnContext,
  dispatchServerTurn,
  getNextRequestId,
  getChatState,
  isTurnBusy,
  lastQuizListSnapshotRef,
  selectedQuizContext,
  submitUndoLastAiAction,
}: UseAiBuilderSubmitAssistantActionParams) => {
  return useCallback(
    (_messageId: string, action: AiBuilderAction) => {
      if (action.type === 'closePanel') {
        closePanel();
        return;
      }

      const prompt = action.prompt.trim();

      if (!prompt || isTurnBusy()) {
        return;
      }

      if (action.type === 'undoLastAiAction') {
        submitUndoLastAiAction(prompt);
        return;
      }

      const usage = getChatState().usage;
      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'assistant_action');
        return;
      }

      const requestId = getNextRequestId();
      const clientRequestId = createAiBuilderClientRequestId();
      const userMessageId = createAiBuilderUserMessageId(requestId);
      const userMessageContent = action.label.trim() || prompt;
      const retryRequest: AiBuilderUserMessageRetryRequest = {
        clientRequestId,
        type: 'message',
        prompt,
        selectedQuizContext,
        sourceRefs: [],
      };

      appendUserMessage({
        content: userMessageContent,
        requestId,
        retry: retryRequest,
      });

      lastQuizListSnapshotRef.current = null;
      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createMessageTurnRequest({
            requestId,
            clientRequestId,
            clientCopy: copy,
            message: prompt,
            context: createTurnContext({ signal, selectedQuizContext }),
          }),
        { userMessageId },
      );
    },
    [
      appendUserMessage,
      closePanel,
      copy,
      createTurnContext,
      dispatchServerTurn,
      getNextRequestId,
      getChatState,
      isTurnBusy,
      lastQuizListSnapshotRef,
      selectedQuizContext,
      submitUndoLastAiAction,
    ],
  );
};
